import React from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { Box, Flex, IconButton, Tooltip, Popover, PopoverContent, PopoverTrigger } from 'pounceV2';
import { useTheme } from '@mui/material';

import { AISummarizeKind, OutputLength } from 'Generated/schema';
import AIPromptSettingsV3, {
  mapOutputLengthToInitial,
  mapOutputLengthToLabel,
} from 'Components/AIExplainLogEvents/AIPromptSettingsV3';
import { getAiOutputLengthSetting } from 'Components/AIExplainLogEvents/GenericAISummaryContextV3';
import AIStopButtonV3 from 'Components/AIExplainLogEvents/AIStopButtonV3';
import AIAttachmentPillsV3 from 'Components/AIExplainLogEvents/AIAttachmentPillsV3';
import { useDragAndDrop } from 'Components/AIExplainLogEvents/hooks/useDragAndDrop';
import type { Attachment } from 'Components/AIExplainLogEvents/hooks/useAIAttachments';
import { ATTACHMENT_ACCEPT } from 'Components/AIExplainLogEvents/utils/attachmentConstants';
import { useInvokableAISkills } from 'Pages/AI/graphql/invokableAISkills.generated';
import { getAISurface } from 'Components/AI/surfaces';

import SkillMentionsFieldV3 from './SkillMentionsFieldV3';
import AddComposerMenuV3 from './AddComposerMenuV3';

interface ConversationInputProps {
  /**
   * Submit handler. May be sync or async. If it throws (or rejects), the typed
   * message is preserved so the user can retry without retyping — the handler
   * owns user-facing error UI. The throw is purely a control-flow signal.
   */
  onSubmit: (message: string, outputLength: OutputLength) => void | Promise<void>;
  kind?: AISummarizeKind;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  isLoading?: boolean;
  cancelPrompt?: () => void;
  onAttachFile?: (file: File) => void;
  isAttachDisabled?: boolean;
  isUploading?: boolean;
  attachments?: Attachment[];
  onRemoveAttachment?: (id: string) => void;
  /** Optional control rendered in the left action group (e.g. the landing page privacy selector). */
  leadingControl?: React.ReactNode;
}

interface ConversationFormValues {
  message: string;
  outputLength: OutputLength;
  kind: AISummarizeKind;
}

const validationSchema = Yup.object().shape({
  message: Yup.string().trim().required(),
});

/**
 * Conversation input component.
 * Two-row composer: textarea on top, action bar on bottom (add menu + attach + settings + send).
 *
 * Skills picker: typing `/` opens a dropdown of invokable skills. Selecting a
 * skill inserts a `/[display](skill_name)` token into the message. The token
 * is forwarded to the backend verbatim — the AI Lambda's SkillMentionsModule
 * detects the picker pattern in the user prompt and injects a system
 * instruction telling the model to invoke the named skill tool before
 * answering the rest. No client-side expansion or prompt fetching.
 */
const ConversationInputV3: React.FC<ConversationInputProps> = ({
  onSubmit,
  kind = AISummarizeKind.Overview,
  placeholder = 'Type a message... (type / to call a skill)',
  disabled = false,
  autoFocus = false,
  isLoading = false,
  cancelPrompt,
  onAttachFile,
  isAttachDisabled,
  isUploading,
  attachments,
  onRemoveAttachment,
  leadingControl,
}) => {
  const {
    palette: { tokens, primary, mode },
  } = useTheme();

  const backgroundColor = getAISurface(tokens, mode);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const canDrop = !!onAttachFile && !isAttachDisabled && !disabled && !isLoading;
  const { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } =
    useDragAndDrop({
      canDrop,
      onDrop: onAttachFile,
    });

  const showAttachmentArea = !!attachments && !!onRemoveAttachment;
  const hasAttachments = showAttachmentArea && attachments.length > 0;

  const { data: skillsData } = useInvokableAISkills({
    context: { failSilently: true },
    fetchPolicy: 'cache-first',
  });
  const skills = React.useMemo(() => skillsData?.invokableAISkills ?? [], [skillsData]);
  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      borderRadius="12px"
      border="1px solid"
      borderColor={isDragOver ? primary.main : tokens.stroke.neutral[5]}
      backgroundColor={backgroundColor}
      overflow="hidden"
      sx={{
        '& textarea::placeholder': {
          color: tokens.content.text.neutral[3],
          opacity: 1,
        },
      }}
    >
      <Formik<ConversationFormValues>
        initialValues={{
          message: '',
          outputLength: getAiOutputLengthSetting(kind),
          kind,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          try {
            await onSubmit(values.message.trim(), values.outputLength);
            resetForm({ values: { ...values, message: '' } });
          } catch {
            // Preserve the typed message + skill chips so the user can retry.
            // The handler already surfaced the error UI.
          }
        }}
      >
        {({ submitForm, isValid, dirty, values }) => {
          const canSubmit =
            isValid && dirty && !disabled && !isLoading && !isUploading && !!values.message.trim();
          return (
            <Form>
              {showAttachmentArea && (
                <Box
                  display="grid"
                  gridTemplateRows={hasAttachments ? '1fr' : '0fr'}
                  transition="grid-template-rows 150ms ease"
                  mx={2}
                >
                  <Box overflow="hidden" minHeight={0}>
                    <Box pt={1.5}>
                      <AIAttachmentPillsV3
                        attachments={attachments}
                        onRemove={onRemoveAttachment}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {/* skills-mention textarea */}
              <Field name="message">
                {({ field, form }: FieldProps<string>) => (
                  <SkillMentionsFieldV3
                    value={field.value}
                    onChange={nextValue => form.setFieldValue('message', nextValue)}
                    onSubmitShortcut={() => {
                      if (canSubmit) {
                        submitForm();
                      }
                    }}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={isLoading || disabled}
                    skills={skills}
                  />
                )}
              </Field>

              {/* action bar */}
              <Flex alignItems="center" justify="space-between" px={2} py={1.5} gap={1}>
                <Flex alignItems="center" gap={1}>
                  <AddComposerMenuV3 disabled={disabled || isLoading} />
                  {leadingControl}
                </Flex>
                <Flex alignItems="center" gap={1}>
                  {/* Attach File Button */}
                  {onAttachFile && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        multiple
                        accept={ATTACHMENT_ACCEPT}
                        onChange={e => {
                          Array.from(e.target.files || []).forEach(f => onAttachFile(f));
                          // Reset so the same file can be re-selected
                          e.target.value = '';
                        }}
                      />
                      <Tooltip content="Attach file">
                        <Box as="span">
                          <IconButton
                            icon="place-file"
                            aria-label="Attach file"
                            size="medium"
                            variant="ghost"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled || isAttachDisabled}
                          />
                        </Box>
                      </Tooltip>
                    </>
                  )}

                  {/* Settings Button with Output Length Badge */}
                  <Popover>
                    <>
                      <Box position="relative" mr={1}>
                        <Tooltip
                          content={`Reasoning level: ${mapOutputLengthToLabel(
                            values.outputLength,
                          )}`}
                        >
                          <Box as="span">
                            <PopoverTrigger
                              as={IconButton}
                              icon="settings-line"
                              aria-label="edit prompt settings"
                              size="medium"
                              variant="ghost"
                              disabled={disabled}
                            />
                          </Box>
                        </Tooltip>
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          px="2px"
                          fontSize="x-small"
                          fontWeight="bold"
                          transform="translate(25%, -25%)"
                          sx={{
                            backgroundColor: tokens.background.neutral.raised[3],
                            color: tokens.content.text.neutral.base,
                          }}
                          minWidth="14px"
                          height="14px"
                          lineHeight="normal"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{ borderRadius: '50%' }}
                        >
                          {mapOutputLengthToInitial(values.outputLength)}
                        </Box>
                      </Box>
                      <PopoverContent alignment="top-center" zIndex={1400}>
                        <AIPromptSettingsV3 outputLength={values.outputLength} />
                      </PopoverContent>
                    </>
                  </Popover>

                  {/* Stop Button (when loading) or Submit Button */}
                  {isLoading && cancelPrompt ? (
                    <AIStopButtonV3 cancelPrompt={cancelPrompt} />
                  ) : (
                    <Tooltip content="Submit">
                      <Box as="span">
                        <IconButton
                          aria-label="Submit"
                          icon="paper-plane"
                          size="medium"
                          type="button"
                          variant="primary"
                          flexShrink={0}
                          onClick={() => submitForm()}
                          disabled={!canSubmit}
                          sx={{
                            boxShadow: `0 4px 12px ${tokens.background.brand.blue[1]}33`,
                            '&.Mui-disabled': {
                              backgroundColor: tokens.background.brand.blue[2],
                              color: tokens.background.basic.white,
                              opacity: 1,
                              boxShadow: `0 4px 12px ${tokens.background.brand.blue[1]}1a`,
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default ConversationInputV3;
